// Global keyboard shortcuts (sprint 9): T = Start timer, Shift+T = Start with details, L = Log time.
// Pure decision logic so every suppression rule is unit-testable without React.

export type ShortcutAction = 'start-timer' | 'start-with-details' | 'log-time';

export interface ShortcutHint {
  /** WAI-ARIA `aria-keyshortcuts` value. */
  aria: string;
  /** Compact visible hint (inside a <kbd>). */
  label: string;
  /** Spoken/tooltip form, e.g. "Shift+T". */
  title: string;
}

export const SHORTCUT_HINTS: Readonly<Record<ShortcutAction, ShortcutHint>> = {
  'start-timer': { aria: 'T', label: 'T', title: 'T' },
  'start-with-details': { aria: 'Shift+T', label: '⇧T', title: 'Shift+T' },
  'log-time': { aria: 'L', label: 'L', title: 'L' },
};

const KEY_START = 't';
const KEY_LOG = 'l';
// Chrome/Safari report keyCode 229 for keydowns that belong to an IME composition.
const IME_PROCESS_KEY_CODE = 229;

// Focus inside any of these means the user is typing or driving a widget that owns letter keys.
const EDITABLE_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="combobox"]',
  '[role="textbox"]',
  '[role="searchbox"]',
  '[role="listbox"]',
  '[role="spinbutton"]',
].join(', ');

// Radix dialogs, sheets, alert dialogs and popovers render role="dialog"/"alertdialog"; dropdown menus
// render role="menu"; autocomplete lists role="listbox". Content that is animating out keeps
// data-state="closed" for a frame and must not block the next shortcut.
const OPEN_OVERLAY_SELECTOR = ['dialog', 'alertdialog', 'menu', 'listbox']
  .map((role) => `[role="${role}"]:not([data-state="closed"])`)
  .concat('dialog[open]')
  .join(', ');

export type ShortcutKeyEvent = Pick<
  KeyboardEvent,
  'key' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey' | 'repeat' | 'isComposing' | 'keyCode' | 'defaultPrevented' | 'target'
>;

function isEditableElement(node: EventTarget | Element | null): boolean {
  if (!(node instanceof Element)) return false;
  if (node instanceof HTMLElement && node.isContentEditable) return true;
  return node.closest(EDITABLE_SELECTOR) !== null;
}

export function isOverlayOpen(doc: Document): boolean {
  return doc.querySelector(OPEN_OVERLAY_SELECTOR) !== null;
}

/** The shortcut this keydown triggers, or null when it must be left alone. */
export function resolveShortcut(event: ShortcutKeyEvent, doc: Document): ShortcutAction | null {
  if (event.defaultPrevented || event.repeat) return null;
  if (event.isComposing || event.keyCode === IME_PROCESS_KEY_CODE) return null;
  // Ctrl/Meta/Alt combos belong to the browser and OS (Ctrl+T new tab, Ctrl+L address bar, Alt+T toasts).
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (typeof event.key !== 'string' || event.key.length !== 1) return null;

  const key = event.key.toLowerCase();
  if (key !== KEY_START && key !== KEY_LOG) return null;
  if (isEditableElement(event.target) || isEditableElement(doc.activeElement)) return null;
  if (isOverlayOpen(doc)) return null;

  // Caps Lock changes event.key's case but not shiftKey, so Shift is read from the flag alone.
  if (key === KEY_START) return event.shiftKey ? 'start-with-details' : 'start-timer';
  return event.shiftKey ? null : 'log-time';
}

// The signed-in share view (/shared/:token) renders inside AppShell but is a read-only report page,
// so it gets no shortcuts (login, invite and the public report are outside the shell already).
const SHORTCUT_FREE_PREFIXES = ['/shared/'];

export function shortcutsAllowedOn(pathname: string): boolean {
  return !SHORTCUT_FREE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}
